export type TrackProps = Record<string, string | number | boolean | undefined>

export type AnalyticsEventPayload = TrackProps & {
  event_name: string
  page_url: string
  page_title: string
  referrer: string
  component?: string
  target?: string
  ts: number
  site?: string
  locale?: string
}

const SITE_ID = 'ldosite_poptira'

function trackGa(eventName: string, props: TrackProps): void {
  const gtag = (globalThis as { gtag?: (...args: unknown[]) => void }).gtag
  if (typeof gtag === 'function') {
    gtag('event', eventName, props)
  }
}

function trackLdcs(eventName: string, props: TrackProps): void {
  const ldcs = (globalThis as { ldcsTrack?: (eventName: string, props: TrackProps) => void }).ldcsTrack
  if (typeof ldcs === 'function') {
    ldcs(eventName, props)
  }
}

export function buildEventPayload(
  eventName: string,
  props: TrackProps = {},
): AnalyticsEventPayload {
  const pageUrl = typeof window !== 'undefined' ? window.location.href : ''
  const pageTitle = typeof document !== 'undefined' ? document.title : ''
  const referrer = typeof document !== 'undefined' ? document.referrer : ''
  const locale =
    typeof window !== 'undefined' && window.PoptiraI18n?.getLocale
      ? window.PoptiraI18n.getLocale()
      : 'en-US'

  return {
    event_name: eventName,
    page_url: pageUrl,
    page_title: pageTitle,
    referrer,
    site: SITE_ID,
    locale,
    ts: Date.now(),
    ...props,
  }
}

const recentEvents = new Set<string>()

export function track(eventName: string, props: TrackProps = {}): void {
  const payload = buildEventPayload(eventName, props)
  const dedupeKey = `${eventName}:${payload.page_url}:${String(payload.target ?? '')}:${String(payload.component ?? '')}`
  if (recentEvents.has(dedupeKey)) {
    return
  }
  recentEvents.add(dedupeKey)
  window.setTimeout(() => recentEvents.delete(dedupeKey), 1000)

  trackGa(eventName, payload)
  trackLdcs(eventName, payload)
}

export function trackPageView(pagePath: string, component = 'page'): void {
  track('page_view', {
    component,
    target: pagePath,
  })
}

export function trackNavClick(target: string, component = 'header_nav'): void {
  track('nav_click', { component, target })
}

export function trackCtaClick(target: string, component = 'hero_cta'): void {
  track('cta_click', { component, target })
}

export function trackDownloadClick(target: string, component = 'downloads_cta'): void {
  track('download_click', { component, target })
}

export function trackOutboundClick(target: string, component = 'outbound_link'): void {
  track('outbound_click', { component, target })
}

export function trackFormSubmit(component = 'contact_form'): void {
  track('form_submit', { component, target: '/contact' })
}
